import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Send, Loader2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useServiceMap, loadLocation } from "@/lib/data";
import { useAuth } from "@/context/AuthContext";
import LocationPicker from "@/components/LocationPicker";
import PhotoPicker from "@/components/PhotoPicker";
import { ServicePicker } from "@/components/ServiceSearch";
import BackButton from "@/components/BackButton";
import PriceInput, { PaymentNote } from "@/components/PriceInput";

export default function NewRequest() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const serviceMap = useServiceMap();
  const saved = loadLocation() || {};
  const providerId = sp.get("prestador");
  const { data: target } = useQuery({
    queryKey: ["provider", providerId],
    queryFn: () => api.get(`/providers/${providerId}`).then((r) => r.data),
    enabled: !!providerId,
  });
  const [form, setForm] = useState({
    service_slug: sp.get("servico") || "",
    city: saved.city || "",
    neighborhood: saved.neighborhood || "",
    description: "",
    price_type: "a_combinar",
    price: "",
    whatsapp: user?.whatsapp || "",
    photos: [],
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_slug) return toast.error("Escolha o serviço.");
    if (!form.city || !form.neighborhood) return toast.error("Escolha a cidade e o bairro.");
    const price = Number(form.price);
    if (form.price_type === "valor" && !(price >= 10 && price <= 20000)) return toast.error("Informe um valor entre R$ 10 e R$ 20.000, ou escolha 'A combinar'.");
    setBusy(true);
    try {
      const body = { ...form, price: form.price_type === "valor" ? price : null, provider_id: providerId || null };
      if (saved.mode === "gps" && saved.city === form.city) Object.assign(body, { lat: saved.lat, lng: saved.lng });
      const { data } = await api.post("/requests", body);
      navigate(`/pedido/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
      <BackButton to={form.service_slug ? `/?servico=${form.service_slug}` : "/"} />
      <h1 className="font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">Pedir orçamento</h1>
      {target && (
        <p className="mt-3 flex items-center gap-2 rounded-2xl bg-orange-50 p-4 text-lg text-slate-800" data-testid="direct-request-banner">
          <UserCheck className="h-6 w-6 text-orange-600" /> Pedido direto para <b>{target.name}</b>
        </p>
      )}
      <form onSubmit={submit} className="mt-6 space-y-6" data-testid="new-request-form">
        <div>
          <p className="po-label">Serviço</p>
          {providerId && serviceMap[form.service_slug] ? (
            <p className="rounded-2xl bg-slate-50 p-4 text-lg font-bold" data-testid="request-service-fixed">{serviceMap[form.service_slug].name}</p>
          ) : (
            <ServicePicker value={form.service_slug} onChange={(service_slug) => setForm({ ...form, service_slug })} testId="request-service" />
          )}
        </div>
        {form.service_slug && <PriceInput type={form.price_type} price={form.price} onChange={(v) => setForm({ ...form, ...v })} />}
        <LocationPicker city={form.city} neighborhood={form.neighborhood} prefix="request" onChange={(v) => setForm({ ...form, ...v })} />
        <div>
          <label className="po-label" htmlFor="desc">Descrição curta (opcional)</label>
          <textarea id="desc" className="po-input min-h-[120px] py-3" maxLength={300} value={form.description} onChange={set("description")}
            placeholder="Ex.: Preciso trocar 3 tomadas e instalar um chuveiro." data-testid="request-description-input" />
          <p className="mt-1 text-right text-sm text-slate-500" data-testid="description-counter">{form.description.length}/300</p>
        </div>
        <div>
          <label className="po-label" htmlFor="wa">Seu WhatsApp</label>
          <input id="wa" className="po-input" type="tel" inputMode="tel" required value={form.whatsapp} onChange={set("whatsapp")} placeholder="(62) 99999-9999" data-testid="request-whatsapp-input" />
          <p className="mt-1 text-base text-slate-600">Só o prestador que aceitar seu pedido verá seu número.</p>
        </div>
        <div>
          <p className="po-label">Fotos (opcional, até 3)</p>
          <PhotoPicker value={form.photos} onChange={(photos) => setForm({ ...form, photos })} max={3} kind="request" />
        </div>
        <PaymentNote />
        <button type="submit" disabled={busy} className="po-btn-primary min-h-[72px] w-full text-xl" data-testid="request-submit-button">
          {busy ? <Loader2 className="h-7 w-7 animate-spin" /> : <Send className="h-7 w-7" />} Enviar pedido
        </button>
      </form>
    </div>
  );
}
